import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row, Tooltip } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType, SelectProps } from 'antd/es/select';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { CustomerApis } from 'apis/CustomerApis';
import { GET_ALL_PLANT } from 'apis/GraphQL/Queries/plantQuery';
import { GET_REGION } from 'apis/GraphQL/Queries/customerQuery';
import { ISearchRouteComboBox } from 'apis/GraphQL/Queries/saleLineQuery';
import axiosGraphQL from 'apis/GraphQL/axiosGraphQL';
import { SaleLineApi } from 'apis/SaleLineApi';
import { FormFilter, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import { IParamQraphQL } from 'interfaces/GraphQL/GraphQL';
import Content from 'layouts/Content';
import { optionStatus } from './ModalSaleLinePage';

interface IProps {
  isOpen: boolean;
  defaultFilter: any;
  onCancel: () => void;
  onFinish: (values: any) => void;
}

const spanCol = 8;

export const sharedProps: SelectProps = {
  mode: 'multiple',
  style: { width: '100%' },
  maxTagCount: 'responsive',
  maxTagPlaceholder: (omittedValues) => (
    <Tooltip
      overlayStyle={{ pointerEvents: 'none' }}
      title={omittedValues.map(({ label }) => label).join(', ')}
    >
      <span>+ {omittedValues.length} ...</span>
    </Tooltip>
  ),
};

const FilterSaleLinePage = forwardRef((props: IProps, ref: any) => {
  const { isOpen, defaultFilter, onFinish, onCancel } = props;

  const [form] = useForm();
  const { user } = useUser();

  useImperativeHandle(ref, () => ({
    handleCancel,
  }));

  const [customersInRoute, setCustomersInRoute] =
      useState<ILazySelectData>(initLazySelectData),
    [routeData, setRouteData] = useState<ILazySelectData>(initLazySelectData);

  const [optionsWardNew, setOptionsWardNew] = useState<DefaultOptionType[]>([]),
    [optionsCityNew, setOptionsCityNew] = useState<DefaultOptionType[]>([]),
    [regionItems, setRegionItems] = useState<any>([]),
    [optionsBranch, setOptionsBranch] = useState<DefaultOptionType[]>([]);

  const mutationBranch = useMutation({
    mutationKey: ['getAllBranch'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      setOptionsBranch(
        res.data?.getPlant
          ? res.data?.getPlant.items.map((item: any) => ({
              label: item.id,
              value: item.id,
            }))
          : [],
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending: getLisRouteCbLoading, mutate: getListRouteCb } =
    useMutation({
      mutationFn: (params: ISearchRouteComboBox) =>
        SaleLineApi.getAllRouteComboBox(params),
      onSuccess: (res) => {
        const items = res.data?.getRouteM?.items ?? [],
          totalItems = res.data?.getRouteM?.pageInfo?.totalItems;

        const options =
          items.length > 0
            ? items.map((item: any) => ({
                label: `${item.id} - ${item.name}`,
                value: item.id,
              }))
            : [];

        setRouteData({
          options: options,
          totalItems: totalItems,
        });
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const { mutate: getCustomersMutate, isPending: isGetCustomersMutatePending } =
    useMutation({
      mutationFn: (params: {
        pageSize: number;
        searchValue: string;
        plantId: string;
      }) => CustomerApis.getCustomersInCombobox(params),
      onSuccess: (res) => {
        if (res?.data?.getCustomer) {
          const data = res?.data?.getCustomer;

          setCustomersInRoute({
            options: data.items.map(
              (item: any) =>
                ({
                  label:
                    item?.id + (item?.storeName ? ` - ${item.storeName}` : ''),
                  value: item?.id,
                }) as DefaultOptionType,
            ),
            totalItems: data?.pageInfo?.totalItems,
          });
        }
      },
    });

  useEffect(() => {
    if (user?.isAdmin || user?.isHO) {
      mutationBranch.mutate({
        query: GET_ALL_PLANT,
        variables: {
          input: {
            id: {
              not: '5000',
            },
          },
        },
      });
    }

    mutationRegion.mutate({
      query: GET_REGION,
      variables: {
        take: 5000,
      },
    });
  }, []);

  useEffect(() => {
    form.setFieldsValue(defaultFilter);
  }, [defaultFilter, form]);

  const handleRouteSelectScrollChange = (params: ILazySelectParams) => {
    const plantId = form.getFieldValue('plantId');
    const routeParams: ISearchRouteComboBox = {
      ...params,
      plantId: plantId,
      ...defaultFilter,
    };

    getListRouteCb(routeParams);
  };

  const handleChangePlant = (value: any) => {
    form.setFieldValue('routeId', undefined);

    getListRouteCb({
      pageSize: 10,
      ...(value && { plantId: value }),
    });
  };

  const mutationRegion = useMutation({
    mutationKey: ['getRegion'],
    mutationFn: (params: IParamQraphQL) =>
      axiosGraphQL.post('/graphql', params),
    onSuccess: (res) => {
      const items = res.data?.getRegion?.items || [];
      setRegionItems(items);

      const citiesMap: { [key: string]: string } = {};
      items.forEach((item: any) => {
        const cityName = item.cityName || item.tcity?.name;
        if (item.cityId && cityName) {
          citiesMap[item.cityId] = cityName;
        }
      });
      const cityOptions = Object.keys(citiesMap).map((id) => ({
        label: citiesMap[id],
        value: id,
      }));
      setOptionsCityNew(cityOptions);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleChangeCityNew = (value: any) => {
    const isArrValid = value.length > 0;

    form.setFieldsValue({
      wardNewId: undefined,
    });
    setOptionsWardNew([]);

    if (isArrValid) {
      const filteredItems = regionItems.filter((item: any) =>
        value.includes(item.cityId),
      );
      const groupMap: {
        [key: string]: { label: string; options: any[] };
      } = {};
      filteredItems.forEach((item: any) => {
        const cId = item.cityId;
        const cityName = item.cityName || item.tcity?.name || cId;
        if (!groupMap[cId]) {
          groupMap[cId] = {
            label: cityName,
            options: [],
          };
        }
        groupMap[cId].options.push({
          label: item.wardName || item.tward?.name,
          value: `${cId}-${item.wardId}`,
        });
      });
      setOptionsWardNew(Object.values(groupMap));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(defaultFilter);

    setOptionsWardNew([]);
    onCancel();
  };

  const handleScrollCustomersChange = useCallback(
    (params: ILazySelectParams) => {
      const { pageSize, value } = params;
      const formData = form.getFieldsValue();

      console.log(formData);

      getCustomersMutate({
        pageSize,
        plantId: formData?.plantId,
        searchValue: value || '',
      });
    },
    [form],
  );

  return (
    <Content isOpen={isOpen}>
      <FormFilter form={form} onFinish={onFinish} onCancel={handleCancel}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                options={optionsBranch}
                disabled={!(user?.isAdmin || user?.isHO)}
                placeholder="Chọn chi nhánh"
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tuyến bán hàng" name="routeId">
              <LazySelectForm
                data={routeData}
                loading={getLisRouteCbLoading}
                placeholder="Chọn mã - tên tuyến"
                onSelectScrollChange={handleRouteSelectScrollChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Trạng thái" name="statusId">
              <SelectForm
                size="large"
                options={optionStatus}
                placeholder="Chọn trạng thái"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Tỉnh/thành" name="cityNewId">
              <SelectForm
                {...sharedProps}
                options={optionsCityNew}
                placeholder="Chọn tỉnh/thành"
                onChange={handleChangeCityNew}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Phường/xã" name="wardNewId">
              <SelectForm
                {...sharedProps}
                options={optionsWardNew}
                placeholder="Chọn khu vực"
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Khách hàng" name="customer">
              {/* <InputForm
                name="customer"
                placeholder="Nhập mã khách hàng, mã DMS, MST"
              /> */}
              <LazySelectForm
                placeholder="Nhập mã khách hàng, mã DMS, MST"
                data={customersInRoute}
                loading={isGetCustomersMutatePending}
                onSelectScrollChange={handleScrollCustomersChange}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
});

export default memo(FilterSaleLinePage);
